/**
 * Typed, validated runtime config sourced from Vite mode env files.
 * Never hardcode environment URLs in feature code — import from here.
 */

export type AppEnvironment = 'development' | 'uat' | 'production';
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface AppConfig {
  env: AppEnvironment;
  appName: string;
  panelOrigin: string;
  apiUrl: string;
  channel: string;
  strictSse: boolean;
  enableMockSse: boolean;
  logLevel: LogLevel;
  isProduction: boolean;
  isUat: boolean;
  isDevelopment: boolean;
}

function readBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

function requireEnv(key: keyof ImportMetaEnv, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `[Avatar Widget] Missing required env "${key}". Check src-panel/.env.<mode>`,
    );
  }
  return value;
}

function resolveAppEnv(raw: string | undefined): AppEnvironment {
  if (raw === 'development' || raw === 'uat' || raw === 'production') {
    return raw;
  }
  // Vite mode fallback when VITE_APP_ENV is omitted
  const mode = import.meta.env.MODE;
  if (mode === 'uat') return 'uat';
  if (mode === 'production') return 'production';
  return 'development';
}

const envName = resolveAppEnv(import.meta.env.VITE_APP_ENV);

export const appConfig: AppConfig = {
  env: envName,
  appName: import.meta.env.VITE_APP_NAME || 'Avatar Widget',
  panelOrigin: requireEnv(
    'VITE_PANEL_ORIGIN',
    import.meta.env.VITE_PANEL_ORIGIN,
  ).replace(/\/$/, ''),
  apiUrl: requireEnv(
    'VITE_AVATAR_API_URL',
    import.meta.env.VITE_AVATAR_API_URL,
  ),
  channel: import.meta.env.VITE_CHANNEL || 'web_widget',
  strictSse: readBoolean(import.meta.env.VITE_STRICT_SSE, envName === 'production'),
  enableMockSse: readBoolean(
    import.meta.env.VITE_ENABLE_MOCK_SSE,
    envName === 'development',
  ),
  logLevel: (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info',
  isProduction: envName === 'production',
  isUat: envName === 'uat',
  isDevelopment: envName === 'development',
};

export function log(
  level: LogLevel,
  message: string,
  detail?: unknown,
): void {
  const order: LogLevel[] = ['debug', 'info', 'warn', 'error'];
  if (order.indexOf(level) < order.indexOf(appConfig.logLevel)) return;

  const prefix = `[Avatar:${appConfig.env}]`;
  if (level === 'error') console.error(prefix, message, detail ?? '');
  else if (level === 'warn') console.warn(prefix, message, detail ?? '');
  else console.info(prefix, message, detail ?? '');
}
